import { tablesDB, DATABASE_ID, STORE_TABLE_ID, PAYMENT_METHOD_TABLE_ID, ID, Query } from '../lib/appwrite';
import AuthService from './authService';

class StoreService {
  /**
   * Create store with all collected data (called at final step)
   * This combines store details, location, and payment information
   */
  static async createStoreWithAllData(storeDetails, locationData, paymentData = null) {
    try {
      // Verify user has valid session first
      // Get current user ID
      const userId = await AuthService.getCurrentUserId();
      // console.log('✅ Creating store for authenticated user:', userId);

      // Generate unique store ID
      const storeId = ID.unique();

      // Prepare store data matching the actual database structure
      // Required fields: storeName, storeId, ownerId, storeSize, district, region, ward, street, tin, surveyed
      
      // Format coordinates as Point [longitude, latitude] for Appwrite Point column
      // Only for surveyed locations
      let coordinates = null;
      const isSurveyed = locationData.surveyed === 'yes' || locationData.surveyed === 'surveyed' || locationData.locationType === 'surveyed';
      
      if (isSurveyed && locationData.longitude && locationData.latitude) {
        coordinates = [
          parseFloat(locationData.longitude),
          parseFloat(locationData.latitude)
        ];
        // console.log('📍 Coordinates formatted:', coordinates);
      }
      
      // Map locationType to surveyed field: 'surveyed' -> 'yes', 'unsurveyed' -> 'no'
      let surveyedValue = 'yes'; // default
      if (locationData.locationType === 'unsurveyed' || locationData.surveyed === 'no' || locationData.surveyed === 'unsurveyed') {
        surveyedValue = 'no';
      } else if (locationData.surveyed) {
        surveyedValue = locationData.surveyed; // Use existing value if provided
      }
      
      const storeRecord = {
        storeId: storeId,
        storeName: storeDetails.storeName || 'My Store', // Required - should be collected in step 1
        ownerId: userId, // Required - links to users.uid
        storeSize: storeDetails.storeSize || 'small', // Required
        tin: storeDetails.tinNumber || '', // Required - Tax Identification Number
        surveyed: surveyedValue, // Required - 'yes' or 'no' (mapped from locationType)
        
        // Location fields - required for surveyed, optional for unsurveyed
        district: locationData.district || '',
        region: locationData.region || '',
        ward: locationData.ward || '',
        street: locationData.street || '',
        
        // Optional fields
        isActive: false, // Store starts inactive until approved
        road: locationData.road || '',
        buildingNumber: locationData.buildingNumber || locationData.houseNo || '',
        contact: storeDetails.phone || storeDetails.contact || '',
        businessNumber: storeDetails.businessRegNo || storeDetails.businessNumber || '',
        documentFile: storeDetails.businessLicenseUrl || storeDetails.documentFile || '',
        unsurveyedAddress: locationData.unsurveyedAddress || '', // For unsurveyed locations
        createdAt: new Date().toISOString(),
        // Add coordinates as Point [longitude, latitude] - only for surveyed locations
        ...(coordinates && { coordinates })
      };

      // console.log('📝 Creating store with data:', storeRecord);

      // Create store row in TablesDB
      const store = await tablesDB.createRow(
        DATABASE_ID,
        STORE_TABLE_ID,
        storeId,
        storeRecord
      );

      // console.log('✅ Store created successfully:', store.$id);

      // Set the newly created store as the active store
      this.setActiveStoreId(store.$id);

      // If payment data is provided, create payment method record
      let paymentMethodId = null;
      if (paymentData && paymentData.accountName && paymentData.accountNumber) {
        try {
          const paymentRecord = {
            storeId: storeId,
            accountName: paymentData.accountName,
            accountNumber: paymentData.accountNumber,
            paymentMethod: paymentData.type || paymentData.paymentMethod || '',
            provider: paymentData.provider || '',
            createdAt: new Date().toISOString()
          };

          paymentMethodId = ID.unique();
          await tablesDB.createRow(
            DATABASE_ID,
            PAYMENT_METHOD_TABLE_ID,
            paymentMethodId,
            paymentRecord
          );
          // console.log('✅ Payment method created successfully:', paymentMethod.$id);
        } catch (paymentError) {
          console.error('⚠️ Failed to create payment method (non-critical):', paymentError);
          // Don't fail the whole registration if payment method creation fails
        }
      }

      return {
        storeId: store.$id,
        store: store,
        paymentMethodId: paymentMethodId,
        registrationStages: {
          userProfile: true,
          storeProfile: true,
          storeLocation: true,
          paymentDetails: !!paymentData
        }
      };
    } catch (error) {
      console.error('Store creation error:', error);
      
      if (error.message === 'No user is currently signed in') {
        throw new Error('Please log in to continue');
      }

      throw new Error(error.message || 'Failed to create store. Please try again.');
    }
  }

  /**
   * Register/store details for a shop (DEPRECATED - use createStoreWithAllData instead)
   * Kept for backward compatibility but should not create store immediately
   */
  static async registerStore(storeData) {
    // This method is deprecated - stores should be created at the final step
    // For now, just save data to localStorage
    // console.warn('⚠️ registerStore is deprecated - data will be saved to localStorage');
    
    // Save store details to localStorage for later use
    const storeDetails = {
      ...storeData,
      timestamp: Date.now()
    };
    localStorage.setItem('storeRegistrationDetails', JSON.stringify(storeDetails));
    
    return {
      storeId: null, // Will be created at final step
      message: 'Store details saved. Please continue to location step.'
    };
  }

  /**
   * Save store location data to localStorage (DEPRECATED - location is now part of store creation)
   * Location data will be included when creating the store at the final step
   */
  static async addStoreLocation(locationData) {
    // This method is deprecated - location data should be saved to localStorage
    // and included when creating the store at the final step
    // console.warn('⚠️ addStoreLocation is deprecated - data will be saved to localStorage');
    
    // Save location details to localStorage for later use
    const locationDetails = {
      ...locationData,
      timestamp: Date.now()
    };
    localStorage.setItem('storeLocationDetails', JSON.stringify(locationDetails));

      return {
      message: 'Location details saved. Please continue to payment step.'
    };
  }

  /**
   * Check store registration progress for current user
   * Returns the next incomplete step or null if all steps are complete
   */
  static async checkRegistrationProgress() {
    try {
      // Verify user has valid session first
      const userId = await AuthService.getCurrentUserId();

      // Check if user has any stores
      const stores = await tablesDB.listRows(
        DATABASE_ID,
        STORE_TABLE_ID,
        [
          Query.equal('ownerId', userId) // Note: field is ownerId, not ownerID
        ]
      );

      const userStores = stores.rows || [];
      
      // If no stores exist, user hasn't started registration
      if (userStores.length === 0) {
        return {
          hasStore: false,
          hasLocation: false,
          hasPayment: false,
          nextStep: '/register/store', // Start from beginning
          storeId: null
        };
      }

      // Get the active store or first store
      let activeStoreId = this.getActiveStoreId();
      let store = userStores.find(s => s.$id === activeStoreId);
      
      // If active store doesn't exist or not set, use first store
      if (!store) {
        store = userStores[0];
        activeStoreId = store.$id;
        this.setActiveStoreId(activeStoreId);
      }
      
      const storeId = store.$id;

      // Check if store has location (location data is in store table, not separate table)
      // Surveyed: needs region, district, ward, street. Unsurveyed: needs unsurveyedAddress
      let hasLocation = false;
      try {
        const hasSurveyedLocation = !!(store.region && store.district && store.ward && store.street);
        const hasUnsurveyedLocation = !!(store.unsurveyedAddress && String(store.unsurveyedAddress).trim());
        hasLocation = hasSurveyedLocation || hasUnsurveyedLocation;
      } catch (error) {
        hasLocation = false;
      }

      // Payment details are optional and can be added later in Shop Settings
      // Determine next step based on store and location completion
      let nextStep = null;
      if (!hasLocation) {
        nextStep = '/register/store/location';
      } else {
        // All required steps complete (store and location)
        nextStep = '/dashboard';
      }

      return {
        hasStore: true,
        hasLocation: hasLocation,
        hasPayment: false, // Payment is optional
        nextStep: nextStep,
        storeId: storeId,
        store: store
      };
    } catch (error) {
      console.error('Error checking registration progress:', error);
      // If error, assume no progress and start from beginning
      return {
        hasStore: false,
        hasLocation: false,
        hasPayment: false,
        nextStep: '/register/store',
        storeId: null
      };
    }
  }

  /**
   * Get stores owned by current user
   */
  static async getMyStores() {
    try {
      // Verify user has valid session first
      const userId = await AuthService.getCurrentUserId();

      const stores = await tablesDB.listRows(
        DATABASE_ID,
        STORE_TABLE_ID,
        [
          Query.equal('ownerId', userId) // Note: field is ownerId, not ownerID
        ]
      );

      return stores.rows || [];
    } catch (error) {
      throw new Error('Failed to get stores');
    }
  }

  /**
   * Get store by ID
   */
  static async getStore(storeId) {
    try {
      // Verify user has valid session first
      const store = await tablesDB.getRow(
        DATABASE_ID,
        STORE_TABLE_ID,
        storeId
      );
      return store;
    } catch (error) {
      throw new Error('Failed to get store');
    }
  }

  /**
   * Get store location (location data is in store table, not separate table)
   */
  static async getStoreLocation(storeId) {
    try {
      // Verify user has valid session first
      const store = await tablesDB.getRow(
        DATABASE_ID,
        STORE_TABLE_ID,
        storeId
      );
      
      // Extract coordinates from Point column [longitude, latitude]
      let latitude = null;
      let longitude = null;
      if (store.coordinates && Array.isArray(store.coordinates) && store.coordinates.length === 2) {
        longitude = store.coordinates[0];
        latitude = store.coordinates[1];
      }
      
      // Return location data from store record
      return {
        region: store.region,
        district: store.district,
        ward: store.ward,
        street: store.street,
        road: store.road,
        buildingNumber: store.buildingNumber,
        unsurveyedAddress: store.unsurveyedAddress,
        surveyed: store.surveyed,
        coordinates: store.coordinates,
        latitude: latitude,
        longitude: longitude
      };
    } catch (error) {
      throw new Error('Failed to get store location');
    }
  }

  /**
   * Update store details
   */
  static async updateStore(storeId, updates) {
    try {
      // Verify user has valid session first
      const updatedStore = await tablesDB.updateRow(
        DATABASE_ID,
        STORE_TABLE_ID,
        storeId,
        updates
      );
      return updatedStore;
    } catch (error) {
      throw new Error('Failed to update store');
    }
  }

  /**
   * Add payment details (saves to localStorage, will be created with store at final step)
   */
  static async addPaymentDetails(paymentData) {
    // Save payment details to localStorage for later use
    const paymentDetails = {
      ...paymentData,
      timestamp: Date.now()
    };
    localStorage.setItem('storePaymentDetails', JSON.stringify(paymentDetails));
    
    return {
      message: 'Payment details saved. Ready to complete registration.'
    };
  }

  /**
   * Get the currently active store ID from localStorage
   */
  static getActiveStoreId() {
    return localStorage.getItem('activeStoreId') || localStorage.getItem('storeId');
  }

  /**
   * Set the active store ID
   */
  static setActiveStoreId(storeId) {
    if (storeId) {
      localStorage.setItem('activeStoreId', storeId);
      localStorage.setItem('storeId', storeId); // Keep for backward compatibility
    } else {
      localStorage.removeItem('activeStoreId');
      localStorage.removeItem('storeId');
    }
  }

  /**
   * Get the currently active store
   */
  static async getActiveStore() {
    try {
      const activeStoreId = this.getActiveStoreId();
      if (!activeStoreId) {
        // If no active store, try to get the first store
        const stores = await this.getMyStores();
        if (stores && stores.length > 0) {
          this.setActiveStoreId(stores[0].$id);
          return stores[0];
        }
        return null;
      }
      return await this.getStore(activeStoreId);
    } catch (error) {
      console.error('Error getting active store:', error);
      return null;
    }
  }
}

export default StoreService;
